'use client';

import { useDetailedNavigation } from '@/components/DetailedView/DetailedNavigationProvider';
import { useMatchMedia } from '@/hooks';
import { FocusTrap } from '@headlessui/react';
import { Button } from '@tallinn-arvudes/ui/tallinn-design/Button';
import { clsx, cn } from '@tallinn-arvudes/ui/utils';
import { useRouter } from 'next/navigation';
import {
  type ComponentProps,
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';

type DialogSize = 'small' | 'medium' | 'large' | 'fluid';
type DialogScroll = 'body' | 'viewport' | 'none';
type DialogButtonVariant = 'primary' | 'danger';

interface DialogProps {
  isOpen?: boolean;
  onAccept?: () => void;
  children: ReactNode;
  size?: DialogSize;
  scroll?: DialogScroll;
  showCloseButton?: boolean;
  acceptLabel?: string;
  closeLabel?: string;
  acceptButtonVariant?: DialogButtonVariant;
}

export const MetricPopup: FC<DialogProps> = ({
  children,
  size = 'fluid',
  scroll = 'body',
}) => {
  const { isMd } = useMatchMedia(['md']);

  const closeRef = useRef<HTMLButtonElement>(null);
  const previousRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);

  const {
    next,
    previous,
    previousAction,
    canNavigateBackward,
    canNavigateForward,
  } = useDetailedNavigation();

  const canNavigate = canNavigateBackward || canNavigateForward;

  const dialogRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const onClose = useCallback(() => {
    router.back();
  }, [router]);

  const onPrevious = useCallback(() => {
    previous();
  }, [previous]);

  const onNext = useCallback(() => {
    next();
  }, [next]);

  useEffect(() => {
    if (!previousAction) {
      return;
    }

    if (previousAction === 'previous' && canNavigateBackward) {
      previousRef?.current?.focus();
    } else if (previousAction === 'next' && canNavigateForward) {
      nextRef?.current?.focus();
    }
  }, [canNavigateBackward, canNavigateForward, previousAction]);

  useEffect(() => {
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
      if (event.key === 'ArrowLeft') {
        onPrevious?.();
      }
      if (event.key === 'ArrowRight') {
        onNext?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, onNext, onPrevious]);

  const dialogClasses = clsx('tds-dialog', {
    [`tds-dialog--${size}`]: size,
    'tds-dialog--scroll-body': scroll === 'body',
    'tds-dialog--scroll-viewport': scroll === 'viewport',
  });

  const overlayClasses = clsx('tds-dialog-overlay', 'is-visible', {
    'is-scrollable': scroll === 'viewport',
  });

  return (
    <div className="fixed z-[60]">
      <FocusTrap
        autoFocus={false}
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
        role="dialog"
        ref={dialogRef}
        aria-modal="true"
        aria-labelledby="dialog-title"
        aria-describedby="dialog-content"
        className={overlayClasses}
      >
        <div
          className={cn(
            'h-full m-0 md:!mx-24 max-h-full md:h-auto md:max-h-[90%] !rounded-none w-full max-w-content relative',
            dialogClasses,
          )}
        >
          <Button
            ref={closeRef}
            onClick={onClose}
            light={isMd}
            variant={isMd ? 'icon-neutral' : 'icon'}
            className="absolute top-[0.75rem] right-3 z-50 md:-top-8 md:-right-[4.5rem]"
          >
            <DialogCloseIcon className="md:!text-white md:hover:!text-white h-8 w-8" />
          </Button>
          {isMd && canNavigate ? (
            <>
              <Button
                disabled={!canNavigateBackward}
                ref={previousRef}
                onClick={onPrevious}
                light
                variant="icon-neutral"
                className="hidden md:block absolute md:translate-y-1/2 md:top-1/2 sm:bottom-4 -left-[4.5rem] rotate-180 md:!text-white md:hover:!text-white"
              >
                <ArrowIcon className="h-8 w-8" />
              </Button>
              <Button
                disabled={!canNavigateForward}
                ref={nextRef}
                onClick={onNext}
                light
                variant="icon-neutral"
                className="hidden md:block absolute md:translate-y-1/2 md:top-1/2 sm:bottom-4 -right-[4.5rem] md:!text-white md:hover:!text-white"
              >
                <ArrowIcon className="h-8 w-8" />
              </Button>
            </>
          ) : null}
          <div id="dialog-content" className="tds-dialog__body !p-0 relative">
            {children}
          </div>
          {!isMd && canNavigate ? (
            <div className="w-full p-4 md:hidden flex justify-between">
              <Button
                disabled={!canNavigateBackward}
                ref={previousRef}
                onClick={onPrevious}
                variant="icon"
                className="h-8 w-8 rotate-180 md:!text-white md:hover:!text-white"
              >
                <ArrowIcon className="h-8 w-8" />
              </Button>
              <Button
                disabled={!canNavigateForward}
                ref={nextRef}
                onClick={onNext}
                variant="icon"
                className="h-8 w-8 md:!text-white md:hover:!text-white"
              >
                <ArrowIcon className="h-8 w-8" />
              </Button>
            </div>
          ) : null}
        </div>
      </FocusTrap>
    </div>
  );
};
const ArrowIcon = (props: ComponentProps<'svg'>) => {
  return (
    <svg
      width="56"
      height="56"
      viewBox="0 0 56 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M6 29.8929L43.1643 29.8929L31.4929 41.5643L33.6429 43.7143L49 28.3572L33.6429 13L31.4929 15.15L43.1643 26.8214L6 26.8214L6 29.8929Z"
        fill="#D6E9F8"
      />
    </svg>
  );
};

const DialogCloseIcon = (props: ComponentProps<'svg'>) => {
  return (
    <svg
      width="62"
      height="62"
      viewBox="0 0 62 62"
      fill="white"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M46.7849 12.4633L31.0002 28.248L15.2155 12.4633L12.4635 15.2153L28.2482 30.9999L12.4633 46.7846L15.2153 49.5366L31.0002 33.7519L46.7849 49.5366L49.5369 46.7847L33.7522 30.9999L49.5369 15.2153L46.7849 12.4633Z"
        fill="#D6E9F8"
      />
    </svg>
  );
};
